import { useCallback, useEffect, useRef } from "react";
import { makeVar, useReactiveVar } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";

/**
 * Reactive variable that signals the PaymentBatchesTable needs to refresh.
 * When true, the table will refresh and reset this to false.
 */
export const paymentBatchesNeedsRefresh = makeVar<boolean>(false);

/**
 * This function triggers a refresh of the PaymentBatchesTable.
 */
export const triggerPaymentBatchesRefresh = (): void => {
  paymentBatchesNeedsRefresh(true);
};

/**
 * This hook will refresh the ag grid table when the reactive variable is set to true.
 * Must be used in conjunction with the onGridReady callback from AG Grid.
 */
export const useTableRefreshFromSignal = () => {
  const needsRefresh = useReactiveVar(paymentBatchesNeedsRefresh);

  const gridApiRef = useRef<AgGrid.GridApi | null>(null);

  const onGridReady = useCallback((event: AgGrid.GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  useEffect(() => {
    if (needsRefresh) {
      gridApiRef.current?.refreshServerSide({ purge: true });
      paymentBatchesNeedsRefresh(false);
    }
  }, [needsRefresh, gridApiRef]);

  return { onGridReady };
};
